/**
 * Clock and duration formatting for the log screen. Pure and local-time
 * based: "today" is whatever day the phone is in. Strings mirror the design
 * exactly ("9:41 pm", "4 hr 34 min", "just now").
 */

export const toDate = (d: Date | string | number): Date => (d instanceof Date ? d : new Date(d));

/** "9:41 pm" */
export function clock(d: Date): string {
  let h = d.getHours();
  const m = d.getMinutes();
  const ap = h < 12 ? "am" : "pm";
  h = h % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ap}`;
}

/** "noon" | "12 am" | "6 pm" — a clock hour (0–23), as the sleep chart's ticks say it. */
export function hourName(hour: number): string {
  if (hour === 12) return "noon";
  return `${hour % 12 || 12} ${hour < 12 ? "am" : "pm"}`;
}

/** "6 pm–7 pm" | "11 am–noon" — the hour that starts at `hour` (0–23). */
export const hourSpan = (hour: number): string => `${hourName(hour)}–${hourName((hour + 1) % 24)}`;

/** "45 min" | "3 hr" | "4 hr 34 min" */
export function minutesText(mins: number): string {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

/** "just now" | "12 min" | "4 hr 34 min" — elapsed from `from` to `now`. */
export function ago(from: Date, now: Date): string {
  const mins = Math.max(0, Math.round((now.getTime() - from.getTime()) / 60_000));
  if (mins < 1) return "just now";
  return minutesText(mins);
}

/** "just now" | "12 min ago" — `ago` as a phrase, without "just now ago". */
export function agoText(from: Date, now: Date): string {
  const elapsed = ago(from, now);
  return elapsed === "just now" ? elapsed : `${elapsed} ago`;
}

/** Duration between two instants, never less than a minute. */
export function dur(a: Date, b: Date): string {
  const mins = Math.max(1, Math.round((b.getTime() - a.getTime()) / 60_000));
  return minutesText(mins);
}

/** "6h 12m" | "45m" | "6h" — the compact form for chart labels. */
export function shortDuration(ms: number): string {
  const mins = Math.max(0, Math.round(ms / 60_000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (!h) return `${m}m`;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** "Tuesday, September 8" */
export function dateline(now: Date): string {
  return now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Mon 28" — a chart row or column label. */
export function weekdayDay(d: Date): string {
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()}`;
}

/** "Sep 27" */
export function monthDay(d: Date): string {
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** Midnight `n` local days after the day `d` is in (DST-safe: calendar arithmetic). */
export function addLocalDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

export function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Local midnight that started the day `d` is in. */
export function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "2026-10-19T23:50": a phone-local time as a `datetime-local` input holds it. */
export function toLocalInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The time a `datetime-local` input holds, or null when it's empty (iOS's Clear). */
export function fromLocalInput(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  const [, y, mo, d, h, mi] = m.map(Number) as [number, number, number, number, number, number];
  return new Date(y, mo - 1, d, h, mi);
}

/**
 * A calendar date (YYYY-MM-DD) from an API response. api.ts turns Eden's date
 * parsing off, so the string sent arrives and passes through. With it on,
 * Eden would read the string as a `Date` at midnight UTC; that midnight's UTC
 * date is the one the server sent, never the phone's.
 */
export function calendarDate(value: unknown): string | null {
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return typeof value === "string" ? value : null;
}

/** The middle value (the mean of the middle two for an even count), or null for none. */
export function median(xs: readonly number[]): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

export const pluralize = (n: number, one: string, many: string): string =>
  `${n} ${n === 1 ? one : many}`;
